// RollsIntlPage — built in the SIGNALS design language (intl-signals.css):
// a responsive grid of premium 3D tilt-cards, one per tier, each leading with the
// combined-odds hero and a nation-flag strip. Click lifts the card into a focus
// overlay (backdrop blur, scale-up) holding the legs and a betting ticket.

import "../../styles/intl-signals.css";
import { useEffect, useRef, useState, type JSX, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { NationFlag, IIcon } from "../international/_intlkit.tsx";
import type { IntlAccaTournament } from "../../db/intl-acca-types";
import { formatOdds } from "../../db/intl-acca-types";
import type { Roll, RollLeg } from "../../intl-acca/useIntlAccaRolls.ts";

type MatchCodes = Record<string, { home: string | null; away: string | null }>;

export interface RollsIntlPageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  rolls: Roll[];
  matchLabels: Record<string, string>;
  matchCodes: MatchCodes;
}

const TIER_WINDOW_FALLBACK: Record<string, string> = {
  SINGLES: "Today", ACCA3: "Next 2 days", ACCA5: "Next 4 days", ACCA10: "Next 8 days",
};
function windowLabel(r: Roll): string {
  if (r.window_start && r.window_end) {
    const fmt = (d: string) => new Date(d + "T12:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    return r.window_start === r.window_end ? fmt(r.window_start) : `${fmt(r.window_start)} – ${fmt(r.window_end)}`;
  }
  return TIER_WINDOW_FALLBACK[r.tier_code] ?? "";
}
const rollOdds = (r: Roll): number =>
  r.combined_odds ?? r.legs.reduce((p, l) => p * (l.market_price_at_selection || 1), 1);
function legSides(label: string): [string, string] {
  const i = label.indexOf(" v ");
  return i === -1 ? [label, ""] : [label.slice(0, i), label.slice(i + 3)];
}

// Count-up for the ticket figures.
function useCountUp(target: number): number {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const from = fromRef.current, to = target;
    if (from === to) { setVal(to); return; }
    const dur = Math.abs(to) >= 1e5 ? 850 : Math.abs(to) >= 1e3 ? 600 : 460;
    const t0 = performance.now();
    const ease = (p: number) => 1 - Math.pow(1 - p, 3);
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      setVal(from + (to - from) * ease(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else { fromRef.current = to; setVal(to); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); };
  }, [target]);
  return val;
}

function StateWrap({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div className="isig-state">
      <div className="isig-state-title">{title}</div>
      <p className="isig-state-body">{body}</p>
    </div>
  );
}

// ── Grid card — one tier, tilts toward the cursor; click opens the overlay. ──
function RollCard({ roll, codes, onOpen }: { roll: Roll; codes: MatchCodes; onOpen: () => void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const odds = rollOdds(roll);
  const isSingle = roll.tier_code === "SINGLES" || roll.leg_count === 1;
  const edge = Math.min(1, Math.max(0.05, Math.log10(Math.max(odds, 1.01)) / 2)); // bigger acca → warmer glow
  const probs = roll.legs.map((l) => l.fair_probability).filter((n): n is number => n != null);
  const avgProb = probs.length ? probs.reduce((a, b) => a + b, 0) / probs.length : null;

  const flags: string[] = [];
  for (const l of roll.legs) {
    const c = codes[l.match_id];
    if (c?.home) flags.push(c.home);
    if (c?.away) flags.push(c.away);
  }
  const uniqFlags = [...new Set(flags)].slice(0, 10);

  const onMove = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty("--ry", `${(px * 9).toFixed(2)}deg`);
    el.style.setProperty("--rx", `${(-py * 9).toFixed(2)}deg`);
    el.style.setProperty("--mx", `${((px + 0.5) * 100).toFixed(1)}%`);
    el.style.setProperty("--my", `${((py + 0.5) * 100).toFixed(1)}%`);
  };
  const onEnter = (): void => { ref.current?.style.setProperty("--lift", "-9px"); };
  const onLeave = (): void => {
    const el = ref.current; if (!el) return;
    el.style.setProperty("--rx", "0deg"); el.style.setProperty("--ry", "0deg"); el.style.setProperty("--lift", "0px");
  };

  return (
    <div
      ref={ref} className="isig-card isig-card--clickable" style={{ "--edge": edge } as CSSProperties}
      onMouseMove={onMove} onMouseEnter={onEnter} onMouseLeave={onLeave}
      onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    >
      <div className="isig-top">
        <span className="isig-time">{windowLabel(roll)}</span>
        <span className="isig-tier">{roll.selection_basis === "MARKET_LED" ? "Uncalibrated" : `${roll.leg_count} ${roll.leg_count === 1 ? "pick" : "legs"}`}</span>
      </div>
      <div className="isig-roll-id">
        <span className="isig-roll-tier">{roll.tier_label}</span>
        <span className="isig-roll-sub">{roll.selection_basis === "MARKET_LED" ? "Uncalibrated · provisional" : isSingle ? "Single selection" : "Accumulator"}</span>
      </div>
      {uniqFlags.length > 0 && (
        <div className="isig-flagstrip">
          {uniqFlags.map((c, i) => <NationFlag key={`${c}-${i}`} code={c} size={18} />)}
        </div>
      )}
      <div className="isig-edge">
        <div className="isig-clv-row">
          <span className="isig-clv gold">×{odds.toFixed(2)}</span>
          <span className="isig-clv-label">{isSingle ? "Selection odds" : "Combined odds"}</span>
        </div>
        {avgProb != null && <div className="isig-bar gold"><span style={{ width: `${Math.min(100, Math.max(6, avgProb * 100))}%` }} /></div>}
      </div>
      <div className="isig-more">
        <span>{avgProb != null ? `avg ${Math.round(avgProb * 100)}% model · ` : ""}the legs · ticket</span>
        <IIcon name="chart" size={13} />
      </div>
    </div>
  );
}

// ── Ticket inside the overlay. ──
function RollTicket({ roll, stake, setStake }: { roll: Roll; stake: number; setStake: (n: number) => void }): JSX.Element {
  const odds = rollOdds(roll);
  const isSingle = roll.tier_code === "SINGLES" || roll.leg_count === 1;
  const winnings = useCountUp(stake * odds);
  const animOdds = useCountUp(odds);
  const chips = [10, 25, 50, 100];
  const oddsList = roll.legs.map((l) => l.market_price_at_selection).filter((n) => Number.isFinite(n));
  const longest = oddsList.length ? Math.max(...oddsList) : 0;
  const shortest = oddsList.length ? Math.min(...oddsList) : 0;
  const probs = roll.legs.map((l) => l.fair_probability).filter((n): n is number => n != null);
  const avgProb = probs.length ? Math.round((probs.reduce((a, b) => a + b, 0) / probs.length) * 100) : null;
  return (
    <div className="isig-tkt">
      <div className="isig-tkt-row">
        <div className="isig-tkt-block">
          <span className="isig-sec-lbl">{isSingle ? "Selection odds" : "Combined odds"}</span>
          <span className="isig-tkt-odds">×{animOdds.toFixed(2)}</span>
        </div>
        <div className="isig-tkt-block">
          <span className="isig-sec-lbl">Your stake</span>
          <div className="isig-tkt-inp">
            <span className="pfx">$</span>
            <input className="isig-tkt-input" type="number" min="0" inputMode="decimal"
              value={stake} onChange={(e) => setStake(Math.max(0, Number(e.target.value) || 0))} />
          </div>
          <div className="isig-tkt-chips">
            {chips.map((c) => <button key={c} className={`isig-tkt-chip ${stake === c ? "on" : ""}`} onClick={() => setStake(c)}>${c}</button>)}
          </div>
        </div>
      </div>
      <div className="isig-tkt-win">
        <span className="isig-sec-lbl">Potential return</span>
        <span className="isig-tkt-win-val">${Math.round(winnings).toLocaleString("en-US")}</span>
      </div>
      <div className="isig-tkt-meta">
        <div className="m"><span className="k">Legs</span><span className="val">{roll.leg_count}</span></div>
        {avgProb != null && <div className="m"><span className="k">Avg model</span><span className="val gold">{avgProb}%</span></div>}
        <div className="m"><span className="k">Longest</span><span className="val">{formatOdds(longest)}</span></div>
        <div className="m"><span className="k">Shortest</span><span className="val">{formatOdds(shortest)}</span></div>
      </div>
    </div>
  );
}

// ── Lift-into-focus overlay: legs + ticket. ──
function RollOverlay({ roll, labels, codes, stake, setStake, onClose }: {
  roll: Roll; labels: Record<string, string>; codes: MatchCodes; stake: number; setStake: (n: number) => void; onClose: () => void;
}): JSX.Element {
  const odds = rollOdds(roll);
  const isSingle = roll.tier_code === "SINGLES" || roll.leg_count === 1;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return (
    <div className="isig-overlay" onClick={onClose}>
      <div className="isig-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="isig-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="isig-modal-head">
          <div className="isig-top">
            <span className="isig-time">{windowLabel(roll)}</span>
            <span className="isig-tier">{roll.tier_label} · {roll.leg_count} {roll.leg_count === 1 ? "pick" : "legs"}</span>
          </div>
          <div className="isig-roll-id">
            <span className="isig-roll-tier">{roll.tier_label}</span>
            <span className="isig-roll-sub">{roll.selection_basis === "MARKET_LED" ? "Uncalibrated · provisional" : isSingle ? "Single selection" : "Accumulator"}</span>
          </div>
          <div className="isig-edge">
            <div className="isig-clv-row">
              <span className="isig-clv gold">×{odds.toFixed(2)}</span>
              <span className="isig-clv-label">{isSingle ? "Selection odds" : "Combined odds"}</span>
            </div>
          </div>
        </div>

        <div className="isig-sec">
          <p className="isig-sec-lbl">{isSingle ? "The selection" : "The legs"}</p>
          <div className="isig-rl-legs">
            {roll.legs.map((l: RollLeg, i: number) => {
              const [home, away] = legSides(labels[l.match_id] ?? l.match_id);
              const c = codes[l.match_id];
              const prob = l.fair_probability != null ? Math.round(l.fair_probability * 100) : null;
              return (
                <div className="isig-rl-leg" key={l.id}>
                  <span className="isig-rl-rank">{String(i + 1).padStart(2, "0")}</span>
                  <div className="isig-rl-main">
                    <div className="isig-rl-fix">
                      <NationFlag code={c?.home} size={16} /><span>{home}</span>
                      <span className="v">v</span>
                      <NationFlag code={c?.away} size={16} /><span>{away}</span>
                    </div>
                    <div className="isig-rl-pick">
                      <span className="pred">{l.selection}</span>
                      {prob != null && <span className="prob">{prob}% model</span>}
                    </div>
                  </div>
                  <span className="isig-rl-odds">{formatOdds(l.market_price_at_selection)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="isig-divider" />

        <div className="isig-sec">
          <p className="isig-sec-lbl">The ticket</p>
          <RollTicket roll={roll} stake={stake} setStake={setStake} />
        </div>
        <p className="isig-tkt-foot">{roll.selection_basis === "MARKET_LED" ? "No independent probability estimate · odds may change · no bet placement" : "Read-only intelligence · no bet placement"}</p>
      </div>
    </div>
  );
}

export function RollsIntlPage(props: RollsIntlPageProps): JSX.Element {
  const { loading, error, tournament, rolls, matchLabels, matchCodes } = props;
  const [open, setOpen] = useState<Roll | null>(null);
  const [stake, setStake] = useState(25);

  return (
    <div>
      <div className="isig-rolls-head">
        <div className="isig-eyebrow">International{tournament ? ` · ${tournament.name}` : ""}</div>
        <h1 className="isig-title">The Rolls</h1>
        <p className="isig-count">
          {rolls.length > 0
            ? `${rolls.length} live tier${rolls.length === 1 ? "" : "s"} · Singles through the Premium Acca.`
            : "No active predictions."}
        </p>
      </div>

      {loading ? (
        <StateWrap title="Loading the rolls…" body="Pulling the current predictions." />
      ) : error ? (
        <StateWrap title="Rolls unavailable" body={error} />
      ) : rolls.length > 0 ? (
        <div className="isig-sec" style={{ marginTop: 28 }}>
          <p className="isig-sec-lbl">Accumulator tiers</p>
          <div className="isig-grid">
            {rolls.map((r) => <RollCard key={r.id} roll={r} codes={matchCodes} onOpen={() => setOpen(r)} />)}
          </div>
        </div>
      ) : (
        <StateWrap title="No ROLLS picks published yet" body="The current fixtures have not cleared the publication check." />
      )}

      {open && <RollOverlay roll={open} labels={matchLabels} codes={matchCodes} stake={stake} setStake={setStake} onClose={() => setOpen(null)} />}
    </div>
  );
}
